/**
 * TokenMapEditor.tsx
 * ====================
 * SME review-and-edit UI for one rule's token block.
 *
 * Renders inside RuleDetailsModal when rule.cat === "std" and the rule
 * has been registered in the entity's token map.
 *
 * Supported editing modes, dispatched on dsl_fn:
 *
 *   "map_values"        → MappingEditor      (key→value table, add/delete/edit)
 *   "regex_replace"     → RegexPairsEditor   (preset / literal-list / reformat / raw)
 *   "chain_transforms"  → ChainStepsEditor   (per-step: toggle, reorder, edit pattern)
 *   "multi_map_replace" → ChainStepsEditor   (same UI — it's the same data shape)
 *   "apply_if"          → ApplyIfEditor      (read-only condition + inner transform editor)
 *   anything else       → ConfigEditor       (read-only JSON fallback)
 *
 * Save endpoints live in app/routers/dq_router.py — every save here
 * round-trips to the server which updates BOTH the token map AND
 * patches rule.te in process_data.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faPencil, faCheck, faXmark,
  faBookOpen, faTriangleExclamation, faCircleInfo,
  faRotateLeft, faFloppyDisk, faFilter,
  faArrowUp, faArrowDown, faToggleOn, faToggleOff,
  faGripVertical, faEyeSlash, faEye, faLink,
  faChevronDown, faChevronRight as faChevronRightIcon,
} from '@fortawesome/free-solid-svg-icons';
import { T } from '../theme/theme';
import {
  adriva_dq_getTokenMap,
  adriva_dq_saveTokenMap,
  TokenMapResponse,
} from '../API';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RegexClassification =
  | { kind: 'preset';        preset_id: string; label: string }
  | { kind: 'literal_list';  tokens: string[]; case_insensitive: boolean;
                             replacement: string; boundaries: 'word' }
  | { kind: 'reformat';      pattern: string; replacement: string;
                             groups: Array<{ index: number; approx_match: string }> }
  | { kind: 'regex_raw';     pattern: string; replacement: string };

export type RegexPair = {
  pattern:         string;
  replacement:     string;
  step_index?:     number;
  // enabled is a UI-only field; when false the step is skipped during
  // DSL reconstruction (saved as a comment in config["disabled_steps"]).
  enabled?:        boolean;
  classification?: RegexClassification;
  condition?:      Record<string, any> | null;
};

export type TokenBlock = {
  rule_id:        string;
  token_type:     string;
  dsl_fn:         string | null;
  field_arg:      any;
  mapping:        Record<string, string>;
  regex_pairs:    RegexPair[];
  config:         Record<string, any>;
  source:         'llm' | 'sme' | 'merged';
  last_updated:   string;
  block_version:  number;
  sme_note?:      string;
  rule_meta?:     {
    cat?: string; d?: string; td?: string; sev?: string;
    act?: string; score?: number; n?: string;
  };
};

type Props = {
  entityId:      string;
  sourcePrefix:  string;
  column:        string;
  ruleId:        string;
  onSaved?:      (updatedBlock: TokenBlock) => void;
};

// ─── Token-type chip palette ──────────────────────────────────────────────────
const TOKEN_TYPE_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  legal_suffix:    { label: 'Legal Suffix',     color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  country_code:    { label: 'Country Code',     color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  currency_code:   { label: 'Currency',         color: T.green,     bg: T.greenBg,     border: T.greenBorder },
  payment_terms:   { label: 'Payment Terms',    color: T.amber,     bg: T.amberBg,     border: T.amberBorder },
  phone_number:    { label: 'Phone',            color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  email_address:   { label: 'Email',            color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  postal_code:     { label: 'Postal Code',      color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  street_address:  { label: 'Street Address',   color: T.amber,     bg: T.amberBg,     border: T.amberBorder },
  address_type:    { label: 'Address Type',     color: T.green,     bg: T.greenBg,     border: T.greenBorder },
  status_enum:     { label: 'Status Enum',      color: T.purple,    bg: T.purpleBg,    border: T.purpleBorder },
  tax_id:          { label: 'Tax ID',           color: T.red,       bg: T.redBg,       border: T.redBorder },
  duns_number:     { label: 'DUNS Number',      color: T.red,       bg: T.redBg,       border: T.redBorder },
  entity_name:     { label: 'Entity Name',      color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  supplier_name:   { label: 'Supplier Name',    color: T.brand,     bg: T.brandLight,  border: T.brandMid },
  unit_of_measure: { label: 'Unit of Measure',  color: T.teal,      bg: T.tealBg,      border: T.tealBorder },
  product_category:{ label: 'Product Category', color: T.amber,     bg: T.amberBg,     border: T.amberBorder },
  generic:         { label: 'Generic',          color: T.textMuted, bg: T.bgSidebar,   border: T.border },
};

const SOURCE_META: Record<string, { label: string; color: string; bg: string }> = {
  llm:     { label: 'AI Generated',  color: T.brand,     bg: T.brandLight },
  sme:     { label: 'SME Curated',   color: T.green,     bg: T.greenBg },
  merged:  { label: 'Merged',        color: T.purple,    bg: T.purpleBg },
};

// ─── Preset recipes ───────────────────────────────────────────────────────────
const PRESET_OPTIONS = [
  { id: 'collapse_whitespace',         label: 'Collapse all whitespace to a single space' },
  { id: 'remove_whitespace',           label: 'Remove all whitespace' },
  { id: 'trim_edges',                  label: 'Trim leading/trailing whitespace' },
  { id: 'keep_digits_only',            label: 'Keep only digits (strip everything else)' },
  { id: 'keep_alphanumeric',           label: 'Keep only letters and digits' },
  { id: 'remove_thousands_commas',     label: 'Remove commas (thousands separators)' },
  { id: 'remove_currency_symbols',     label: 'Remove leading currency symbols' },
  { id: 'drop_trailing_zero_decimals', label: 'Drop trailing .00' },
  { id: 'slashes_to_dashes',           label: 'Replace slashes with dashes' },
];

// ─── Classification helpers ───────────────────────────────────────────────────
function inferClassification(pair: { pattern: string; replacement: string }): RegexClassification {
  const { pattern, replacement } = pair;
  const stripped   = pattern.replace(/^\(\?i\)/, '');
  const hasGroup   = /\((?!\?)/.test(stripped);
  const hasBackref = /\$\d|\\\d/.test(replacement);
  if (hasGroup && hasBackref) return { kind: 'reformat', pattern, replacement, groups: [] };
  return { kind: 'regex_raw', pattern, replacement };
}

function normalizeRegexPairs(pairs: RegexPair[] | undefined | null): RegexPair[] {
  return (pairs || []).map((p, i) => ({
    ...p,
    step_index: p.step_index ?? i,
    enabled:    p.enabled    ?? true,
    classification: p.classification ?? inferClassification(p),
  }));
}

// ─── Condition label (mirrors token_map_service._condition_label) ─────────────
function conditionLabel(expr: Record<string, any> | null | undefined): string {
  if (!expr || typeof expr !== 'object') return '';
  const { fn, args = [] } = expr;
  const field = String(args[0] ?? '?');
  if (fn === 'equals'       && args[1] !== undefined) return `${field} = "${args[1]}"`;
  if (fn === 'in_list'      && args[1])               return `${field} in ${JSON.stringify(args[1])}`;
  if (fn === 'not_in_list'  && args[1])               return `${field} not in ${JSON.stringify(args[1])}`;
  if (fn === 'not_null')                              return `${field} is not null`;
  if (fn === 'is_null')                               return `${field} is null`;
  if (fn === 'regex_match'  && args[1])               return `${field} matches "${args[1]}"`;
  const ops: Record<string,string> = {
    greater_than: '>', greater_equal: '>=', less_than: '<', less_equal: '<=',
  };
  if (ops[fn] && args[1] !== undefined) return `${field} ${ops[fn]} ${args[1]}`;
  return `${fn}(${args.map(String).join(', ')})`;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const TokenMapEditor: React.FC<Props> = ({
  entityId, sourcePrefix, column, ruleId, onSaved,
}) => {
  const [block,   setBlock]   = useState<TokenBlock | null>(null);
  const [draft,   setDraft]   = useState<TokenBlock | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [search,  setSearch]  = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    adriva_dq_getTokenMap(entityId, sourcePrefix, column, ruleId)
      .then((j: TokenMapResponse) => {
        if (!j.success) throw new Error(j.detail || 'Load failed');
        const data = j.data as TokenBlock;
        data.regex_pairs = normalizeRegexPairs(data.regex_pairs);
        setBlock(data);
        setDraft(JSON.parse(JSON.stringify(data)));
      })
      .catch((e: any) => {
        const msg = e?.detail || e?.message || String(e);
        setError(`Token map not yet built for this rule. Re-run DQ generation to populate it. (${msg})`);
        setBlock(null);
        setDraft(null);
      })
      .finally(() => setLoading(false));
  }, [entityId, sourcePrefix, column, ruleId]);

  const dirty = useMemo(() => {
    if (!block || !draft) return false;
    return JSON.stringify(block) !== JSON.stringify(draft);
  }, [block, draft]);

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      const j = await adriva_dq_saveTokenMap(
        entityId, sourcePrefix, column, ruleId,
        {
          mapping:     draft.mapping,
          regex_pairs: draft.regex_pairs,
          config:      draft.config,
          dsl_fn:      draft.dsl_fn,
          field_arg:   draft.field_arg,
          token_type:  draft.token_type,
          sme_note:    draft.sme_note ?? null,
        },
      );
      if (!j.success) throw new Error(j.detail || 'Save failed');
      const fresh = j.data as TokenBlock;
      fresh.regex_pairs = normalizeRegexPairs(fresh.regex_pairs);
      setBlock(fresh);
      setDraft(JSON.parse(JSON.stringify(fresh)));
      onSaved?.(fresh);
    } catch (e: any) {
      setError(e?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (block) setDraft(JSON.parse(JSON.stringify(block)));
  };

  if (loading) {
    return (
      <div style={{ padding: 30, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
        Loading token map…
      </div>
    );
  }

  if (error || !block || !draft) {
    return (
      <div style={{
        padding: '14px 16px', background: T.amberBg,
        border: `1px solid ${T.amberBorder}`, borderRadius: 10,
        color: T.amber, fontSize: 12,
      }}>
        <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />
        {error}
      </div>
    );
  }

  const fn = (draft.dsl_fn || '').toLowerCase();

  return (
    <div style={{
      border: `1px solid ${T.border}`, borderRadius: 12,
      background: T.bgCard, overflow: 'hidden',
    }}>
      <Header block={draft} dirty={dirty} />

      <div style={{ padding: 16 }}>
        {fn === 'map_values' && (
          <MappingEditor
            mapping={draft.mapping}
            search={search}
            onSearch={setSearch}
            onChange={(m) => setDraft({ ...draft, mapping: m })}
          />
        )}

        {fn === 'regex_replace' && (
          <RegexPairsEditor
            pairs={draft.regex_pairs}
            onChange={(p) => setDraft({ ...draft, regex_pairs: p })}
          />
        )}

        {/* chain_transforms and multi_map_replace both use ChainStepsEditor.
            The data shape is identical: regex_pairs[] with step_index + enabled.
            The only visual difference is the header label, controlled by isChain. */}
        {(fn === 'chain_transforms' || fn === 'multi_map_replace') && (
          <ChainStepsEditor
            pairs={draft.regex_pairs}
            chainSteps={draft.config?.chain_steps || []}
            fieldArg={String(draft.field_arg || '')}
            isChain={fn === 'chain_transforms'}
            onChange={(pairs) => setDraft({ ...draft, regex_pairs: pairs })}
          />
        )}

        {fn === 'apply_if' && (
          <ApplyIfEditor
            draft={draft}
            onChange={(d) => setDraft(d)}
          />
        )}

        {!['map_values', 'regex_replace', 'chain_transforms',
            'multi_map_replace', 'apply_if'].includes(fn) && (
          <ConfigEditor
            dslFn={draft.dsl_fn || ''}
            config={draft.config}
            onChange={(cfg) => setDraft({ ...draft, config: cfg })}
          />
        )}

        {/* SME note */}
        <div style={{ marginTop: 16 }}>
          <label style={labelStyle}>SME Note (optional)</label>
          <textarea
            value={draft.sme_note || ''}
            onChange={e => setDraft({ ...draft, sme_note: e.target.value })}
            placeholder="Why this edit? Visible to other reviewers and audit trail."
            style={{
              width: '100%', minHeight: 50, padding: '8px 10px',
              border: `1px solid ${T.border}`, borderRadius: 8,
              fontSize: 12, color: T.textPrimary, background: T.bgCard,
              outline: 'none', resize: 'vertical' as const, fontFamily: 'inherit',
            }}
          />
        </div>
      </div>

      <Footer dirty={dirty} saving={saving} onSave={handleSave} onDiscard={handleDiscard} />

      {error && (
        <div style={{
          padding: '8px 16px', background: T.redBg,
          border: `1px solid ${T.redBorder}`, color: T.red, fontSize: 12,
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// CHAIN STEPS EDITOR
// Handles both chain_transforms and multi_map_replace.
//
// Each "step" in the token block is a regex_pairs entry with:
//   step_index  — execution order (0-based)
//   enabled     — whether this step runs (SME toggle)
//   pattern     — the regex pattern
//   replacement — the replacement string
//   classification — kind metadata for display
//
// Non-regex steps from chain_transforms (trim, normalize_case, pad_left, etc.)
// are stored in config.chain_steps and shown read-only between the editable
// regex steps so the SME can see the full picture.
//
// SME capabilities:
//   ① Toggle on/off — disable a step without deleting it (gray out the row)
//   ② Reorder — move any step up or down (reassigns step_index)
//   ③ Edit pattern/replacement inline
//   ④ Add new regex step
//   ⑤ Delete a step
// =============================================================================

type ChainStep =
  | { kind: 'regex';    pair: RegexPair; pairIndex: number }
  | { kind: 'nonregex'; fn: string; config: any; step_index: number };

function ChainStepsEditor({
  pairs, chainSteps, fieldArg, isChain, onChange,
}: {
  pairs:       RegexPair[];
  chainSteps:  Array<{ fn: string; config: any; step_index: number }>;
  fieldArg:    string;
  isChain:     boolean;
  onChange:    (pairs: RegexPair[]) => void;
}) {
  // Merge regex pairs and non-regex steps into a single ordered list,
  // sorted by step_index so the SME sees the actual execution order.
  const merged: ChainStep[] = useMemo(() => {
    const result: ChainStep[] = [];

    // Regex steps
    pairs.forEach((p, i) => {
      result.push({ kind: 'regex', pair: p, pairIndex: i });
    });

    // Non-regex context steps (read-only)
    (chainSteps || []).forEach(cs => {
      // Don't show if there's already a regex step at this index
      const clash = pairs.some(p => (p.step_index ?? 0) === cs.step_index);
      if (!clash) {
        result.push({ kind: 'nonregex', fn: cs.fn, config: cs.config || {}, step_index: cs.step_index });
      }
    });

    result.sort((a, b) => {
      const ai = a.kind === 'regex' ? (a.pair.step_index ?? a.pairIndex) : a.step_index;
      const bi = b.kind === 'regex' ? (b.pair.step_index ?? b.pairIndex) : b.step_index;
      return ai - bi;
    });

    return result;
  }, [pairs, chainSteps]);

  const enabledCount  = pairs.filter(p => p.enabled !== false).length;
  const disabledCount = pairs.filter(p => p.enabled === false).length;

  // ── Mutations ──────────────────────────────────────────────────────────────

  const toggleStep = (pairIndex: number) => {
    const next = pairs.map((p, i) =>
      i === pairIndex ? { ...p, enabled: !(p.enabled ?? true) } : p
    );
    onChange(next);
  };

  const moveStep = (pairIndex: number, direction: 'up' | 'down') => {
    // Find the regex steps sorted by their current step_index
    const indexed = pairs
      .map((p, i) => ({ p, i, si: p.step_index ?? i }))
      .sort((a, b) => a.si - b.si);

    const pos = indexed.findIndex(x => x.i === pairIndex);
    if (direction === 'up'   && pos === 0)                  return;
    if (direction === 'down' && pos === indexed.length - 1) return;

    const swapPos = direction === 'up' ? pos - 1 : pos + 1;
    // Swap step_index values
    const siA = indexed[pos].si;
    const siB = indexed[swapPos].si;

    const next = pairs.map((p, i) => {
      if (i === indexed[pos].i)     return { ...p, step_index: siB };
      if (i === indexed[swapPos].i) return { ...p, step_index: siA };
      return p;
    });
    onChange(next);
  };

  const updatePair = (pairIndex: number, update: Partial<RegexPair>) => {
    onChange(pairs.map((p, i) => i === pairIndex ? { ...p, ...update } : p));
  };

  const deletePair = (pairIndex: number) => {
    onChange(pairs.filter((_, i) => i !== pairIndex));
  };

  const addStep = () => {
    const maxSi = pairs.reduce((m, p) => Math.max(m, p.step_index ?? 0), -1);
    const newPair: RegexPair = {
      pattern:     '',
      replacement: '',
      step_index:  maxSi + 1,
      enabled:     true,
      classification: { kind: 'regex_raw', pattern: '', replacement: '' },
    };
    onChange([...pairs, newPair]);
  };

  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <div style={{ fontSize: 12, color: T.textMuted }}>
          <strong style={{ color: T.textPrimary }}>{merged.length}</strong> step{merged.length !== 1 ? 's' : ''}
          {isChain ? ' in pipeline' : ' in replacement list'}
          {disabledCount > 0 && (
            <span style={{
              marginLeft: 8, fontSize: 10, fontWeight: 700,
              padding: '1px 7px', borderRadius: 8,
              background: T.amberBg, color: T.amber,
            }}>
              {disabledCount} disabled
            </span>
          )}
        </div>
        <button onClick={addStep} style={primaryBtnStyle}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: 9, marginRight: 5 }} />
          Add step
        </button>
      </div>

      {/* Info banner for chain_transforms */}
      {isChain && chainSteps.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 12px',
          background: T.brandLight, border: `1px solid ${T.brandMid}`,
          borderRadius: 8, fontSize: 11, color: T.brand,
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <FontAwesomeIcon icon={faCircleInfo} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            Gray rows are non-regex steps (trim, normalize_case, etc.) — shown
            for context but not directly editable here. Use AI Tune to modify them.
          </span>
        </div>
      )}

      {merged.length === 0 && (
        <div style={{
          padding: '20px', textAlign: 'center', color: T.textMuted, fontSize: 12,
          border: `1px dashed ${T.border}`, borderRadius: 8,
        }}>
          No steps yet. Click "Add step" to create the first regex pattern.
        </div>
      )}

      {/* Step list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {merged.map((step, listIdx) => {
          if (step.kind === 'nonregex') {
            return (
              <NonRegexStepRow
                key={`nr-${step.step_index}`}
                fn={step.fn}
                config={step.config}
                stepIndex={step.step_index}
              />
            );
          }

          const { pair, pairIndex } = step;
          const isFirst = listIdx === 0 || merged[listIdx - 1].kind === 'nonregex';
          const isLast  = listIdx === merged.length - 1 || merged[listIdx + 1].kind === 'nonregex';

          return (
            <RegexStepRow
              key={`rx-${pairIndex}`}
              pair={pair}
              pairIndex={pairIndex}
              stepIndex={pair.step_index ?? pairIndex}
              isFirst={isFirst}
              isLast={isLast}
              onToggle={() => toggleStep(pairIndex)}
              onMoveUp={() => moveStep(pairIndex, 'up')}
              onMoveDown={() => moveStep(pairIndex, 'down')}
              onUpdate={(u) => updatePair(pairIndex, u)}
              onDelete={() => deletePair(pairIndex)}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Non-regex step row (read-only) ──────────────────────────────────────────
function NonRegexStepRow({
  fn, config, stepIndex,
}: { fn: string; config: any; stepIndex: number }) {
  const label: Record<string, string> = {
    trim:             'Trim whitespace',
    remove_whitespace:'Remove all whitespace',
    normalize_case:   `Normalize case → ${config?.style || 'title'}`,
    pad_left:         `Pad left to ${config?.length || '?'} with "${config?.char || '0'}"`,
    pad_right:        `Pad right to ${config?.length || '?'} with "${config?.char || '0'}"`,
    substring:        `Substring from ${config?.start ?? 0}, len ${config?.length ?? '?'}`,
    parse_date:       `Parse date (${config?.format || 'auto'})`,
    round:            `Round to ${config?.decimals ?? 2} decimals`,
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderRadius: 8,
      background: T.bgSidebar, border: `1px solid ${T.border}`,
      opacity: 0.75,
    }}>
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9, fontWeight: 700, color: T.textMuted,
        minWidth: 48,
      }}>
        STEP {stepIndex}
      </span>
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
        background: T.border, color: T.textMuted,
      }}>
        {fn}
      </span>
      <span style={{ fontSize: 11, color: T.textMuted, flex: 1 }}>
        {label[fn] || fn}
      </span>
      <span style={{
        fontSize: 9, padding: '1px 6px', borderRadius: 4,
        background: T.bgCard, color: T.textMuted, border: `1px solid ${T.border}`,
      }}>
        read-only
      </span>
    </div>
  );
}

// ─── Regex step row (editable) ────────────────────────────────────────────────
function RegexStepRow({
  pair, pairIndex, stepIndex, isFirst, isLast,
  onToggle, onMoveUp, onMoveDown, onUpdate, onDelete,
}: {
  pair:        RegexPair;
  pairIndex:   number;
  stepIndex:   number;
  isFirst:     boolean;
  isLast:      boolean;
  onToggle:    () => void;
  onMoveUp:    () => void;
  onMoveDown:  () => void;
  onUpdate:    (u: Partial<RegexPair>) => void;
  onDelete:    () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const enabled = pair.enabled !== false;

  const cls   = pair.classification as any;
  const kind  = cls?.kind || 'regex_raw';
  const kindColors: Record<string, { color: string; bg: string; label: string }> = {
    preset:       { color: T.green,     bg: T.greenBg,     label: 'Preset' },
    literal_list: { color: T.brand,     bg: T.brandLight,  label: 'Word list' },
    reformat:     { color: T.amber,     bg: T.amberBg,     label: 'Reformat' },
    regex_raw:    { color: T.purple,    bg: T.purpleBg,    label: 'Regex' },
  };
  const km = kindColors[kind] || kindColors.regex_raw;

  // Condition display (for apply_if inner steps)
  const cond = pair.condition;

  return (
    <div style={{
      border: `1px solid ${enabled ? T.border : T.border}`,
      borderRadius: 8,
      background: enabled ? T.bgCard : T.bgSidebar,
      opacity: enabled ? 1 : 0.55,
      transition: 'opacity .2s, background .2s',
    }}>
      {/* ── Compact row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
      }}>
        {/* Step index badge */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, color: T.textMuted,
          minWidth: 42,
        }}>
          STEP {stepIndex}
        </span>

        {/* Kind badge */}
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
          background: km.bg, color: km.color, whiteSpace: 'nowrap' as const,
        }}>
          {km.label}
        </span>

        {/* Pattern preview — truncated */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, color: enabled ? T.textPrimary : T.textMuted,
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
        }} title={pair.pattern}>
          {pair.pattern || <em style={{ color: T.textMuted }}>no pattern</em>}
        </span>

        <span style={{ fontSize: 11, color: T.textMuted }}>→</span>

        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 700, color: enabled ? T.brand : T.textMuted,
          maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
        }} title={pair.replacement}>
          {pair.replacement === '' ? <em style={{ fontWeight: 400, color: T.textMuted }}>(delete)</em> : pair.replacement}
        </span>

        {/* Condition chip */}
        {cond && (
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
            background: T.tealBg, color: T.teal,
            whiteSpace: 'nowrap' as const, maxWidth: 120,
            overflow: 'hidden', textOverflow: 'ellipsis',
          }} title={`Only when: ${conditionLabel(cond)}`}>
            when: {conditionLabel(cond)}
          </span>
        )}

        {/* Controls */}
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
          {/* Toggle enabled/disabled */}
          <button onClick={onToggle} style={iconBtnStyle}
            title={enabled ? 'Disable this step' : 'Enable this step'}>
            <FontAwesomeIcon
              icon={enabled ? faToggleOn : faToggleOff}
              style={{ fontSize: 14, color: enabled ? T.green : T.textMuted }}
            />
          </button>

          {/* Reorder */}
          <button onClick={onMoveUp} disabled={isFirst} style={{
            ...iconBtnStyle, opacity: isFirst ? 0.3 : 1,
          }} title="Move up">
            <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: 10, color: T.textMuted }} />
          </button>
          <button onClick={onMoveDown} disabled={isLast} style={{
            ...iconBtnStyle, opacity: isLast ? 0.3 : 1,
          }} title="Move down">
            <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: 10, color: T.textMuted }} />
          </button>

          {/* Expand / collapse inline editor */}
          <button onClick={() => setExpanded(v => !v)} style={iconBtnStyle}
            title={expanded ? 'Collapse' : 'Edit pattern'}>
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRightIcon}
              style={{ fontSize: 10, color: T.brand }}
            />
          </button>

          {/* Delete */}
          <button onClick={onDelete} style={iconBtnStyle} title="Delete step">
            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10, color: T.red }} />
          </button>
        </div>
      </div>

      {/* ── Inline expanded editor ── */}
      {expanded && (
        <div style={{
          padding: '10px 12px 12px',
          borderTop: `1px solid ${T.border}`,
          background: T.bgSidebar,
        }}>
          <StepInlineEditor pair={pair} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}

// ─── Inline step editor (pattern + replacement + tester) ─────────────────────
function StepInlineEditor({
  pair, onUpdate,
}: {
  pair:     RegexPair;
  onUpdate: (u: Partial<RegexPair>) => void;
}) {
  const [localPat, setLocalPat] = useState(pair.pattern);
  const [localRep, setLocalRep] = useState(pair.replacement);

  // Commit on blur / Enter
  const commit = () => {
    onUpdate({
      pattern:     localPat,
      replacement: localRep,
      classification: inferClassification({ pattern: localPat, replacement: localRep }),
    });
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Regex pattern</label>
          <input
            value={localPat}
            onChange={e => setLocalPat(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); }}
            placeholder="(?i)\\bIncorporated\\.?$"
            style={{ ...inlineInputStyle, width: '100%', fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
        <span style={{ alignSelf: 'flex-end', paddingBottom: 8, fontSize: 14, color: T.brand }}>→</span>
        <div>
          <label style={labelStyle}>Replacement</label>
          <input
            value={localRep}
            onChange={e => setLocalRep(e.target.value)}
            onBlur={commit}
            onKeyDown={e => { if (e.key === 'Enter') commit(); }}
            placeholder="Inc"
            style={{ ...inlineInputStyle, width: '100%', fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
      </div>
      <PreviewTester pair={{ ...pair, pattern: localPat, replacement: localRep }} />
    </div>
  );
}

// =============================================================================
// APPLY_IF EDITOR
// Shows the condition as read-only, then delegates the inner transform
// to the appropriate editor based on config's inferred inner_fn.
// =============================================================================

function ApplyIfEditor({
  draft, onChange,
}: {
  draft:    TokenBlock;
  onChange: (d: TokenBlock) => void;
}) {
  const condition  = draft.config?.condition;
  const condLabel  = draft.config?.condition_label || conditionLabel(condition);
  const chainSteps = draft.config?.chain_steps || [];

  // Determine what kind of inner editor to show.
  // We look at what data is present: regex_pairs → ChainStepsEditor or RegexPairsEditor;
  // mapping → MappingEditor; otherwise read-only.
  const hasRegexPairs = (draft.regex_pairs || []).length > 0;
  const hasMapping    = Object.keys(draft.mapping || {}).length > 0;

  return (
    <div>
      {/* Condition badge — read-only */}
      <div style={{
        marginBottom: 14, padding: '10px 14px',
        background: T.tealBg, border: `1px solid ${T.tealBorder}`,
        borderRadius: 8, fontSize: 12,
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
          textTransform: 'uppercase' as const, color: T.teal, marginBottom: 4,
        }}>
          Applies only when
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>
          {condLabel || 'condition not available'}
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>
          The condition logic is read-only. To change it, use AI Tune.
        </div>
      </div>

      {/* Inner transform editor */}
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
        textTransform: 'uppercase' as const, color: T.textMuted,
        marginBottom: 8,
      }}>
        Inner transformation (editable)
      </div>

      {hasRegexPairs && (
        <ChainStepsEditor
          pairs={draft.regex_pairs}
          chainSteps={chainSteps}
          fieldArg={String(draft.field_arg || '')}
          isChain={chainSteps.length > 0}
          onChange={(pairs) => onChange({ ...draft, regex_pairs: pairs })}
        />
      )}

      {!hasRegexPairs && hasMapping && (
        <MappingEditor
          mapping={draft.mapping}
          search=""
          onSearch={() => {}}
          onChange={(m) => onChange({ ...draft, mapping: m })}
        />
      )}

      {!hasRegexPairs && !hasMapping && (
        <div style={{
          padding: '12px 14px', background: T.bgSidebar,
          border: `1px solid ${T.border}`, borderRadius: 8,
          fontSize: 11, color: T.textMuted,
        }}>
          Inner transform has no extractable content. Use AI Tune to regenerate.
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EXISTING EDITORS (unchanged from original)
// =============================================================================

// ─── Header ──────────────────────────────────────────────────────────────────
function Header({ block, dirty }: { block: TokenBlock; dirty: boolean }) {
  const ttMeta  = TOKEN_TYPE_META[block.token_type] || TOKEN_TYPE_META.generic;
  const srcMeta = SOURCE_META[block.source] || SOURCE_META.llm;

  return (
    <div style={{
      padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
      background: T.bgSidebar,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      flexWrap: 'wrap' as const, gap: 10,
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const }}>
        <FontAwesomeIcon icon={faBookOpen} style={{ fontSize: 13, color: T.brand }} />
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, fontWeight: 700, color: T.textMuted, letterSpacing: '.05em',
        }}>
          TOKEN MAP
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 9,
          background: ttMeta.bg, color: ttMeta.color, border: `1px solid ${ttMeta.border}`,
        }}>
          {ttMeta.label}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 9,
          background: srcMeta.bg, color: srcMeta.color,
        }}>
          {srcMeta.label}
        </span>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10, padding: '2px 8px', borderRadius: 6,
          background: T.bgCard, color: T.textMuted, border: `1px solid ${T.border}`,
        }}>
          v{block.block_version}
        </span>
        {block.dsl_fn && (
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10, padding: '2px 8px', borderRadius: 6,
            background: T.bgCard, color: T.textPrimary, border: `1px solid ${T.border}`,
          }}>
            fn: {block.dsl_fn}
          </span>
        )}
      </div>
      {dirty && (
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
          background: T.amberBg, color: T.amber, border: `1px solid ${T.amberBorder}`,
        }}>
          Unsaved changes
        </span>
      )}
    </div>
  );
}

// ─── Mapping editor (unchanged) ──────────────────────────────────────────────
function MappingEditor({
  mapping, search, onSearch, onChange,
}: {
  mapping:  Record<string, string>;
  search:   string;
  onSearch: (s: string) => void;
  onChange: (m: Record<string, string>) => void;
}) {
  const [newRaw, setNewRaw] = useState('');
  const [newCanonical, setNewCanonical] = useState('');

  const entries = useMemo(() => {
    const all = Object.entries(mapping);
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter(([k, v]) => k.toLowerCase().includes(q) || String(v).toLowerCase().includes(q));
  }, [mapping, search]);

  const canonicalSet = useMemo(() => new Set(Object.values(mapping)), [mapping]);

  const handleEdit   = (raw: string, canonical: string) => onChange({ ...mapping, [raw]: canonical });
  const handleDelete = (raw: string) => { const m = { ...mapping }; delete m[raw]; onChange(m); };
  const handleAdd    = () => {
    if (!newRaw.trim() || !newCanonical.trim()) return;
    if (newRaw in mapping) return;
    onChange({ ...mapping, [newRaw.trim()]: newCanonical.trim() });
    setNewRaw(''); setNewCanonical('');
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            value={search}
            onChange={e => onSearch(e.target.value)}
            placeholder="Search raw or canonical…"
            style={{ width: '100%', padding: '6px 10px', fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 8, outline: 'none' }}
          />
        </div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: T.textMuted }}>
          {entries.length} / {Object.keys(mapping).length} • {canonicalSet.size} canonicals
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 12px', marginBottom: 12, background: T.brandLight, border: `1px dashed ${T.brand}`, borderRadius: 8 }}>
        <input placeholder="Raw value" value={newRaw} onChange={e => setNewRaw(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} style={inlineInputStyle} />
        <span style={{ fontSize: 14, color: T.brand }}>→</span>
        <input placeholder="Canonical" value={newCanonical} onChange={e => setNewCanonical(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} style={inlineInputStyle} />
        <button onClick={handleAdd} style={primaryBtnStyle}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: 9, marginRight: 5 }} />Add
        </button>
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, maxHeight: 360, overflowY: 'auto' as const }}>
        {entries.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>
            {search ? 'No matches.' : 'No entries yet.'}
          </div>
        )}
        {entries.map(([raw, canonical]) => (
          <MappingRow key={raw} raw={raw} canonical={String(canonical)}
            isIdentity={raw === canonical}
            onEdit={(c) => handleEdit(raw, c)} onDelete={() => handleDelete(raw)} />
        ))}
      </div>
    </div>
  );
}

function MappingRow({ raw, canonical, isIdentity, onEdit, onDelete }: {
  raw: string; canonical: string; isIdentity: boolean;
  onEdit: (c: string) => void; onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(canonical);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: `1px solid ${T.border}`, background: isIdentity ? T.bgSidebar : T.bgCard }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: T.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={raw}>
        {raw === '' ? <em style={{ color: T.textMuted }}>(blank)</em> : raw}
      </span>
      <span style={{ fontSize: 13, color: T.textMuted }}>→</span>
      {editing ? (
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onBlur={() => { onEdit(draft); setEditing(false); }}
          onKeyDown={e => { if (e.key === 'Enter') { onEdit(draft); setEditing(false); } if (e.key === 'Escape') { setDraft(canonical); setEditing(false); } }}
          autoFocus style={{ ...inlineInputStyle, flex: 1 }} />
      ) : (
        <span onClick={() => setEditing(true)} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, flex: 1, color: isIdentity ? T.textMuted : T.brand, cursor: 'pointer', padding: '3px 6px', borderRadius: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={canonical}>
          {canonical}
          {isIdentity && <span style={{ fontSize: 9, marginLeft: 6, padding: '1px 5px', borderRadius: 4, background: T.border, color: T.textMuted }}>identity</span>}
        </span>
      )}
      <button onClick={onDelete} style={iconBtnStyle}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 10, color: T.red }} /></button>
    </div>
  );
}

// ─── Regex pairs editor (for standalone regex_replace rules) ─────────────────
function RegexPairsEditor({ pairs, onChange }: { pairs: RegexPair[]; onChange: (p: RegexPair[]) => void }) {
  const updatePair = (idx: number, next: RegexPair) => { const c = pairs.slice(); c[idx] = next; onChange(c); };
  const removePair = (idx: number) => onChange(pairs.filter((_, i) => i !== idx));
  return (
    <div>
      {pairs.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No transformations defined.</div>}
      {pairs.map((pair, idx) => (
        <PairCard key={idx} index={idx} pair={pair} onChange={(p) => updatePair(idx, p)} onRemove={() => removePair(idx)} />
      ))}
    </div>
  );
}

function PairCard({ index, pair, onChange, onRemove }: { index: number; pair: RegexPair; onChange: (p: RegexPair) => void; onRemove: () => void }) {
  const classification = pair.classification!;
  const k = classification.kind;
  const kindMeta: Record<string, { label: string; color: string; bg: string }> = {
    preset:       { label: 'Preset cleanup', color: T.green,  bg: T.greenBg },
    literal_list: { label: 'Word list',      color: T.brand,  bg: T.brandLight },
    reformat:     { label: 'Format pattern', color: T.amber,  bg: T.amberBg },
    regex_raw:    { label: 'Advanced regex', color: T.purple, bg: T.purpleBg },
  };
  const m = kindMeta[k] || kindMeta.regex_raw;
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginBottom: 10, background: T.bgCard }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: T.textMuted }}>STEP {index + 1}</span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 9, background: m.bg, color: m.color }}>{m.label}</span>
        <div style={{ flex: 1 }} />
        <button onClick={onRemove} style={iconBtnStyle}><FontAwesomeIcon icon={faTrash} style={{ fontSize: 11, color: T.red }} /></button>
      </div>
      {k === 'preset'       && <PresetEditor       pair={pair} onChange={onChange} />}
      {k === 'literal_list' && <LiteralListEditor  pair={pair} onChange={onChange} />}
      {k === 'reformat'     && <ReformatEditor     pair={pair} />}
      {k === 'regex_raw'    && <RegexRawEditor     pair={pair} />}
      <PreviewTester pair={pair} />
    </div>
  );
}

function PresetEditor({ pair, onChange }: { pair: RegexPair; onChange: (p: RegexPair) => void }) {
  const c = pair.classification as Extract<RegexClassification, { kind: 'preset' }>;
  return (
    <div>
      <label style={labelStyle}>Cleanup recipe</label>
      <select value={c.preset_id} onChange={e => { const opt = PRESET_OPTIONS.find(o => o.id === e.target.value)!; onChange({ ...pair, classification: { kind: 'preset', preset_id: opt.id, label: opt.label } }); }} style={{ ...inlineInputStyle, width: '100%' }}>
        {PRESET_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </div>
  );
}

function LiteralListEditor({ pair, onChange }: { pair: RegexPair; onChange: (p: RegexPair) => void }) {
  const c = pair.classification as Extract<RegexClassification, { kind: 'literal_list' }>;
  const [draft, setDraft] = useState('');
  const setTokens = (tokens: string[]) => onChange({ ...pair, classification: { ...c, tokens } });
  const addToken = () => { const t = draft.trim(); if (!t || c.tokens.includes(t)) { setDraft(''); return; } setTokens([...c.tokens, t]); setDraft(''); };
  return (
    <div>
      <label style={labelStyle}>Match any of these words {c.case_insensitive && '(case-insensitive)'}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6, padding: 10, border: `1px solid ${T.border}`, borderRadius: 8, background: T.bgSidebar, minHeight: 40, marginBottom: 10 }}>
        {c.tokens.map(tok => (
          <span key={tok} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '3px 8px', borderRadius: 12, background: T.brandLight, color: T.brand, border: `1px solid ${T.brandMid}` }}>
            {tok}
            <button onClick={() => setTokens(c.tokens.filter(x => x !== tok))} style={{ ...iconBtnStyle, padding: 0 }}><FontAwesomeIcon icon={faXmark} style={{ fontSize: 9, color: T.brand }} /></button>
          </span>
        ))}
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addToken(); } }} onBlur={addToken} placeholder={c.tokens.length === 0 ? 'Type a word and press Enter' : '+ add'} style={{ flex: 1, minWidth: 100, border: 'none', outline: 'none', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: 'transparent' }} />
      </div>
      <label style={labelStyle}>Replace each match with</label>
      <input value={c.replacement} onChange={e => onChange({ ...pair, classification: { ...c, replacement: e.target.value } })} placeholder="(empty to delete)" style={{ ...inlineInputStyle, width: '100%' }} />
      <label style={{ ...labelStyle, marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={c.case_insensitive} onChange={e => onChange({ ...pair, classification: { ...c, case_insensitive: e.target.checked } })} />
        Match regardless of case
      </label>
    </div>
  );
}

function ReformatEditor({ pair }: { pair: RegexPair }) {
  const c = pair.classification as Extract<RegexClassification, { kind: 'reformat' }>;
  return (
    <div>
      <div style={{ background: T.amberBg, border: `1px solid ${T.amberBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 11, color: T.amber, marginBottom: 10 }}>
        <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 6 }} />
        This rule reformats values using captured groups. Use the preview to verify behavior, or ask AI Tune to regenerate.
      </div>
      <div style={readonlyBoxStyle}>
        <div><strong>Match:</strong> <code>{c.pattern}</code></div>
        <div><strong>Output:</strong> <code>{c.replacement}</code></div>
        {c.groups?.length > 0 && <div style={{ marginTop: 6, fontSize: 11, color: T.textMuted }}>{c.groups.map(g => <div key={g.index}>${g.index} = {g.approx_match}</div>)}</div>}
      </div>
    </div>
  );
}

function RegexRawEditor({ pair }: { pair: RegexPair }) {
  const c = pair.classification as Extract<RegexClassification, { kind: 'regex_raw' }>;
  return (
    <div>
      <div style={{ background: T.purpleBg, border: `1px solid ${T.purpleBorder}`, borderRadius: 8, padding: '8px 12px', fontSize: 11, color: T.purple, marginBottom: 10 }}>
        <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 6 }} />
        Advanced pattern — editing requires regex knowledge. Test below before saving.
      </div>
      <div style={readonlyBoxStyle}>
        <div><strong>Pattern:</strong> <code>{c.pattern}</code></div>
        <div><strong>Replacement:</strong> <code>{c.replacement || '(empty)'}</code></div>
      </div>
    </div>
  );
}

// ─── Preview tester ───────────────────────────────────────────────────────────
function PreviewTester({ pair }: { pair: RegexPair }) {
  const [samples, setSamples] = useState('');
  const effective = useMemo(() => effectivePatternFor(pair), [pair]);
  const results = useMemo(() => {
    if (!samples.trim()) return [];
    const lines = samples.split('\n').filter(l => l.length > 0);
    let pat: RegExp | null = null;
    try {
      const flags = effective.pattern.startsWith('(?i)') ? 'gi' : 'g';
      const cleanPat = effective.pattern.replace(/^\(\?i\)/, '');
      pat = new RegExp(cleanPat, flags);
    } catch {
      return lines.map(l => ({ input: l, output: '⚠ invalid pattern', changed: false }));
    }
    return lines.map(l => { const out = l.replace(pat!, effective.replacement); return { input: l, output: out, changed: out !== l }; });
  }, [samples, effective.pattern, effective.replacement]);

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${T.border}` }}>
      <label style={labelStyle}>Test on real values (one per line)</label>
      <textarea value={samples} onChange={e => setSamples(e.target.value)} placeholder={'Paste sample values here\nOne per line'} style={{ width: '100%', minHeight: 50, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '6px 8px', border: `1px solid ${T.border}`, borderRadius: 6, background: T.bgCard, resize: 'vertical' as const, outline: 'none' }} />
      {results.length > 0 && (
        <div style={{ marginTop: 8, border: `1px solid ${T.border}`, borderRadius: 6, maxHeight: 180, overflowY: 'auto' as const }}>
          {results.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, padding: '5px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : 'none', background: r.changed ? T.greenBg : T.bgSidebar }}>
              <span style={{ color: T.textMuted }}>{r.input}</span>
              <span style={{ color: r.changed ? T.green : T.textMuted }}>→</span>
              <span style={{ color: r.changed ? T.green : T.textMuted, fontWeight: 700 }}>{r.output || '(empty)'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function effectivePatternFor(pair: RegexPair): { pattern: string; replacement: string } {
  const c = pair.classification;
  if (!c) return { pattern: pair.pattern, replacement: pair.replacement };
  if (c.kind === 'preset') {
    const recipes: Record<string, { pattern: string; replacement: string }> = {
      collapse_whitespace: { pattern: '\\s+', replacement: ' ' }, remove_whitespace: { pattern: '\\s+', replacement: '' }, trim_edges: { pattern: '^\\s+|\\s+$', replacement: '' }, keep_digits_only: { pattern: '[^0-9]', replacement: '' }, keep_alphanumeric: { pattern: '[^A-Za-z0-9]', replacement: '' }, remove_thousands_commas: { pattern: ',', replacement: '' }, remove_currency_symbols: { pattern: '^\\s*[$€£¥]\\s*', replacement: '' }, drop_trailing_zero_decimals: { pattern: '\\.0+\\s*$', replacement: '' }, slashes_to_dashes: { pattern: '/', replacement: '-' },
    };
    return recipes[c.preset_id] || { pattern: pair.pattern, replacement: pair.replacement };
  }
  if (c.kind === 'literal_list') {
    if (!c.tokens?.length) return { pattern: '', replacement: c.replacement };
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const body   = c.tokens.map(t => `\\b${escape(t)}\\b`).join('|');
    return { pattern: (c.case_insensitive ? '(?i)' : '') + body, replacement: c.replacement };
  }
  return { pattern: c.pattern, replacement: c.replacement };
}

// ─── Config editor (unchanged) ───────────────────────────────────────────────
function ConfigEditor({ dslFn, config, onChange }: { dslFn: string; config: Record<string, any>; onChange: (c: Record<string, any>) => void }) {
  if (dslFn === 'normalize_case') {
    const styles = ['lower', 'upper', 'title', 'sentence'];
    return (
      <div>
        <label style={labelStyle}>Casing Style</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {styles.map(s => { const active = (config.style || 'title') === s; return ( <button key={s} onClick={() => onChange({ ...config, style: s })} style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, background: active ? T.brand : T.bgCard, color: active ? '#fff' : T.textPrimary, border: `1px solid ${active ? T.brand : T.border}`, cursor: 'pointer' }}>{s}</button> ); })}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={labelStyle}>Config (read-only for {dslFn})</div>
      <pre style={{ margin: 0, padding: 12, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: T.textPrimary, background: T.bgSidebar, border: `1px solid ${T.border}`, borderRadius: 8 }}>
        {JSON.stringify(config, null, 2)}
      </pre>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>
        Editing this dsl_fn's config isn't supported here. Use AI Tuning Assistant.
      </div>
    </div>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer({ dirty, saving, onSave, onDiscard }: { dirty: boolean; saving: boolean; onSave: () => void; onDiscard: () => void }) {
  if (!dirty) return null;
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '10px 16px', borderTop: `1px solid ${T.border}`, background: T.bgSidebar }}>
      <button onClick={onDiscard} disabled={saving} style={secondaryBtnStyle}>
        <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 10, marginRight: 5 }} />Discard
      </button>
      <button onClick={onSave} disabled={saving} style={primaryBtnStyle}>
        <FontAwesomeIcon icon={faFloppyDisk} style={{ fontSize: 10, marginRight: 5 }} />
        {saving ? 'Saving…' : 'Save & Sync DSL'}
      </button>
    </div>
  );
}

// ─── Style atoms ──────────────────────────────────────────────────────────────
const inlineInputStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: '5px 10px',
  border: `1px solid ${T.border}`, borderRadius: 6, background: T.bgCard,
  outline: 'none', flex: 1, minWidth: 0,
};
const primaryBtnStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, padding: '5px 14px', borderRadius: 6,
  background: T.brand, color: '#fff', border: 'none', cursor: 'pointer',
  whiteSpace: 'nowrap' as const, display: 'inline-flex', alignItems: 'center',
};
const secondaryBtnStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, padding: '5px 14px', borderRadius: 6,
  background: T.bgCard, color: T.textPrimary, border: `1px solid ${T.border}`,
  cursor: 'pointer', whiteSpace: 'nowrap' as const, display: 'inline-flex', alignItems: 'center',
};
const iconBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const labelStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700,
  letterSpacing: '.1em', textTransform: 'uppercase' as const, color: T.textMuted,
  display: 'block', marginBottom: 6,
};
const readonlyBoxStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace", fontSize: 11, padding: 10,
  background: T.bgSidebar, border: `1px solid ${T.border}`, borderRadius: 6,
  color: T.textPrimary, lineHeight: 1.7,
};

export default TokenMapEditor;